import { useEffect, useState } from 'react';
import { message, Table, Button, Card, Space, Row, Col, Input, Typography, Collapse, Divider } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import "./roles.scss";
import UsersServices from '../../../services/usersServices';
import { tablePageSize } from '../../../utils/constants';
import NewRole from './newRole';
import UpdateRole from './updateRole';
import { usePageTitle } from '../../../context/pageTitleContext';
import { useTranslation } from 'react-i18next';
import { Role, RoleFilter } from './roles.types';

const { Link } = Typography;

const initialFilters: RoleFilter = {
    roleName: '' as string
};

const RolesList = () => {
    const { t } = useTranslation();
    const { setPageTitle } = usePageTitle();
    const [filters, setFilters] = useState<RoleFilter>(initialFilters);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loadingData, setLoadingData] = useState(false);
    const [openCreateDrawer, setOpenCreateDrawer] = useState(false);
    const [openUpdateDrawer, setOpenUpdateDrawer] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);


    useEffect(() => {
        setPageTitle(t('page.roles'));
        getRolesList(filters);
    }, []);

    const showDrawer = (drawerName: string, record: any) => {
        if (drawerName === "create") setOpenCreateDrawer(true);
        if (drawerName === "update") setOpenUpdateDrawer(true); setSelectedRecord(record);
    }

    const onCloseDrawer = (drawerName: string) => {
        if (drawerName === "create") setOpenCreateDrawer(false);
        if (drawerName === "update") setOpenUpdateDrawer(false);
    };

    const getRolesList = async (filters: RoleFilter) => {
        setLoadingData(true);
        try {
            const respData = await UsersServices.getRoles(filters);
            if (respData.success) {
                const res = respData.responseObject.value.rolesList as Role[];
                setRoles(res);
                setLoadingData(false);
            }
            else {
                setLoadingData(false);
                 message.error(t('messages.errorAccured'));
            }
        } catch (error) {
             message.error(t('messages.errorAccured'));
        } finally {
            setLoadingData(false);
        }
    };

    const onFilter = () => {
        getRolesList(filters);
    }

    const onClearFilters = () => {
        setFilters(initialFilters);
        getRolesList(initialFilters);
    }

    const handelFilterChange = (name: string, value: any) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
    }

    const columns = [
        {
            title: t('roles.name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('roles.description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            key: "action",
            width: "50px",
            render: (_: any, record: Role) => {
                return (
                    <Space>
                        <Link onClick={() => showDrawer("update", record)}><EditOutlined className='editIcon' /></Link >
                    </Space>
                )
            }
        }

    ];

    const panelItems = [
        {
            key: '1',
            label: `${t('general.filters')}`,
            children: (
                <>
                    <Row gutter={16}>
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} lg={{ span: 4 }} >
                            <Space direction='vertical'>
                                <span>{t('roles.name')}</span>
                                <Input
                                    value={filters.roleName ?? ''}
                                    onChange={(e) => handelFilterChange("roleName", e.target.value)}
                                />
                            </Space>
                        </Col>
                    </Row>

                    <Divider />
                    <Row gutter={{ lg: 24 }}>
                        <Col className="gutter-row flexAlignEnd" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Button type="primary" style={{ marginRight: 10 }} onClick={() => onFilter()}> {t('general.filter')} </Button>
                            <Button onClick={() => onClearFilters()}> {t('general.clear')}  </Button>
                        </Col>
                    </Row>
                </>
            )
        }
    ]

    return (
        <div className='usersList'>
            <NewRole open={openCreateDrawer} onClose={() => onCloseDrawer("create")} getRoles={() => getRolesList(filters)} />

            {selectedRecord && (
                <UpdateRole open={openUpdateDrawer} onClose={() => onCloseDrawer("update")} getRoles={() => getRolesList(filters)} recordData={selectedRecord} />
            )}

            <Row gutter={{ lg: 24 }}>
                <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                    <Collapse size='small' className='filterPanel' items={panelItems} defaultActiveKey={['1']} />
                </Col>
            </Row>

            <Divider />

            <Card size='small' extra={<Button size='small' type="default" onClick={() => showDrawer("create", null)} icon={<PlusOutlined />}>{t('roles.addNewRole')}</Button>}>
                <Table
                    dataSource={roles}
                    columns={columns}
                    rowKey={obj => obj.id}
                    pagination={{
                        pageSize: tablePageSize
                    }}
                    loading={loadingData}
                    size='small'
                    bordered
                />
            </Card>
        </div>
    )
}

export default RolesList;